import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  addTeamMeta,
  deleteTeamMeta,
  getAllTeamMeta,
  getTeamMetaByID,
  updateTeamMeta,
} from "./teamMetaService";
import { applyLicenseKey } from "../../../../../../utils/License";
import { getAllDepartment } from "../../../../../Team/components/stepper/OfficialInformation/OfficialInfoService";
import ConfirmationModel from "../../../../../../global/components/ConfirmationModal/confirmationModel";
import customErrorHandler from "../../../../../../utils/customErrorHandler";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../../../../../helpers/methods";
import notifiers from "../../../../../../global/constants/NotificationConstants";
import { getAllHierarchyMeta } from "../CompanyHierarchyList/companyHierarchyService";
import YhInput from "../../../../../../ui/YhInput";
import YhSelect from "../../../../../../ui/YhSelect";
import YhButton from "../../../../../../ui/YhButton";
import YhTable, {
  YhTableColumn,
  YhTableCellParams,
} from "../../../../../../ui/YhTable";
import OrganizationSelect, {
  useOrgNameMap,
} from "../../../../../../Shared/Organization/OrganizationSelect";
import {
  listAllOrganizationTeams,
  listTeamOrganizations,
  syncTeamOrganizations,
} from "../../../../../../services/organizationService";
import { useSelector } from "react-redux";
import { selectActiveOrganizationId } from "../../../../../../redux/authSlice";

const Chevron = ({ open }: { open: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={`shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
  >
    <path d="M9 18l6-6-6-6" />
  </svg>
);

type FlatTeam = {
  id: string;
  team_name: string;
  departmentName: string;
  parentTeamId: string | null;
  path: string[];
  childCount: number;
  organizationIds?: string[];
};

const flattenData = (
  data: any[],
  parentPath: string[] = []
): FlatTeam[] => {
  const flattened: FlatTeam[] = [];

  data.forEach((item) => {
    const currentPath = [...parentPath, item.team_name];
    flattened.push({
      id: item._id,
      team_name: item.team_name,
      departmentName: item.departmentName,
      parentTeamId: item.parentTeamId || null,
      path: currentPath,
      childCount: (item.sub_teams || []).length,
    });

    if (item.sub_teams?.length) {
      flattened.push(...flattenData(item.sub_teams, currentPath));
    }
  });

  return flattened;
};

const TeamMetaList = () => {
  const [teamData, setTeamData] = useState<FlatTeam[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [teamMetaDrawer, setTeamMetaDrawer] = useState(false);
  const [departmentList, setDepartmentList] = useState<any[]>([]);
  const [teamDrawerData, setTeamDrawerData] = useState<any>({});
  const [isChecked, setIsChecked] = useState(false);
  const [openTeamModal, setOpenTeamModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<string>("");
  const [selectedRow, setSelectedRow] = useState<null | any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [teamRole, setTeamRole] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(
    null
  );
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [errors, setErrors] = useState<{
    teamName: boolean;
    department: boolean;
  }>({
    teamName: false,
    department: false,
  });
  const [organizationIds, setOrganizationIds] = useState<string[]>([]);
  const orgNameMap = useOrgNameMap();
  const activeOrganizationId = useSelector(selectActiveOrganizationId);

  const headerOrgIds = (): string[] => {
    if (
      activeOrganizationId &&
      activeOrganizationId !== "all" &&
      String(activeOrganizationId).trim()
    ) {
      return [String(activeOrganizationId)];
    }
    return [];
  };

  useEffect(() => {
    applyLicenseKey();
    init();
    handleTableData();
  }, []);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const init = async () => {
    try {
      const response = await getAllDepartment();
      setDepartmentList(
        (response.data || []).map((i: any) => ({
          value: i._id,
          label: i.department_name,
        }))
      );

      const hierarchyResponse = await getAllHierarchyMeta();
      const teamRoleData = (hierarchyResponse.data || []).filter(
        (item: any) => item.isOrganization === 0
      );
      setTeamRole(
        teamRoleData.map((i: any) => ({
          id: i._id,
          label: i.hierarchyName,
          hierarchyLevel: i.hierarchyLevel,
        }))
      );
    } catch (error: any) {
      openErrorNotification(error.message || notifiers.GENERIC_ERROR);
    }
  };

  const handleTableData = async () => {
    try {
      setLoading(true);
      const [response, orgTeamsRes] = await Promise.all([
        getAllTeamMeta(),
        listAllOrganizationTeams().catch(() => ({ data: [] })),
      ]);
      const orgMap: Record<string, string[]> = {};
      (orgTeamsRes?.data || []).forEach((row: any) => {
        const tid = String(row.teamId);
        if (!orgMap[tid]) orgMap[tid] = [];
        orgMap[tid].push(String(row.organizationId));
      });
      const flattenedData = flattenData(response.data || []).map((t) => ({
        ...t,
        organizationIds: orgMap[t.id] || [],
      }));
      setTeamData(flattenedData);

      setExpanded((prev) => {
        const next: Record<string, boolean> = {};
        flattenedData.forEach((row) => {
          if (!row.parentTeamId && row.childCount > 0) {
            next[row.id] = prev[row.id] ?? true;
          }
          const hasKids = flattenedData.some((r) => r.parentTeamId === row.id);
          if (hasKids) {
            next[row.id] = prev[row.id] ?? true;
          }
        });
        return next;
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const visibleRows = useMemo(() => {
    const filtered = teamData.filter((row) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        row.team_name?.toLowerCase().includes(q) ||
        row.departmentName?.toLowerCase().includes(q)
      );
    });

    if (search.trim()) return filtered;

    return filtered.filter((row) => {
      if (!row.parentTeamId) return true;
      // show if all ancestors expanded
      let parentId: string | null = row.parentTeamId;
      while (parentId) {
        if (!expanded[parentId]) return false;
        const parent = teamData.find((t) => t.id === parentId);
        parentId = parent?.parentTeamId || null;
      }
      return true;
    });
  }, [teamData, expanded, search]);

  const closeDrawer = () => {
    setTeamMetaDrawer(false);
    setTeamDrawerData({});
    setIsChecked(false);
    setOrganizationIds([]);
    setErrors({ teamName: false, department: false });
  };

  const openCreate = () => {
    setTeamDrawerData({});
    setIsChecked(false);
    setOrganizationIds(headerOrgIds());
    setErrors({ teamName: false, department: false });
    setTeamMetaDrawer(true);
  };

  const handleParentTeam = (team: FlatTeam) => {
    setSelectedTeam(team.id);
    setTeamDrawerData({
      ...teamDrawerData,
      parentTeamName: team.team_name,
      parentTeamId: team.id,
    });
    setOpenTeamModal(false);
  };

  const toggleRole = (option: {
    id: string;
    label: string;
    hierarchyLevel: any;
  }) => {
    const current = teamDrawerData?.teamRole || [];
    const isSelected = current.some((m: any) => m.id === option.id);
    const updated = isSelected
      ? current.filter((m: any) => m.id !== option.id)
      : [
          ...current,
          {
            id: option.id,
            role: option.label,
            hierarchyLevel: option.hierarchyLevel,
          },
        ];
    setTeamDrawerData((prev: any) => ({ ...prev, teamRole: updated }));
  };

  const handleSubmit = async () => {
    const hasTeamNameError = !teamDrawerData.team_name;
    const hasDepartmentError = !teamDrawerData.departmentId;

    if (hasTeamNameError || hasDepartmentError) {
      setErrors({
        teamName: hasTeamNameError,
        department: hasDepartmentError,
      });
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        departmentId: teamDrawerData.departmentId,
        team_name: teamDrawerData.team_name,
        parentTeamId: isChecked ? teamDrawerData.parentTeamId : null,
        teamRole: teamDrawerData.teamRole,
        id: teamDrawerData._id || null,
        organizationIds,
      };

      const response = !payload.id
        ? await addTeamMeta(payload)
        : await updateTeamMeta(payload);

      if (response.success === 1) {
        const teamId =
          response?.data?._id ||
          payload.id ||
          teamDrawerData._id ||
          null;
        if (teamId && organizationIds.length) {
          await syncTeamOrganizations(String(teamId), organizationIds);
        }
        await handleTableData();
        closeDrawer();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    const id = selectedRow?.id || teamDrawerData?.id;
    if (!id) return;
    try {
      const response = await deleteTeamMeta(id);
      customErrorHandler(response);
      openSuccessNotification(response.message);
      if (response.success === 1) {
        handleTableData();
      }
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setDialogOpen(false);
      setSelectedRow(null);
    }
  };

  const columns: YhTableColumn[] = [
    {
      headerName: "Team Name",
      field: "team_name",
      minWidth: 320,
      flex: 1,
      renderCell: (params: YhTableCellParams) => {
        const row = params.row as FlatTeam;
        const depth = Math.max(0, (row.path?.length || 1) - 1);
        const hasChildren = teamData.some((t) => t.parentTeamId === row.id);
        const isOpen = !!expanded[row.id];

        return (
          <div
            className="flex items-center gap-2"
            style={{ paddingLeft: depth * 20 }}
          >
            <button
              type="button"
              className={`flex h-6 w-6 items-center justify-center rounded text-[#6B7280] ${
                hasChildren ? "hover:bg-[#F3F4F6]" : "invisible"
              }`}
              onClick={(e) => {
                e.stopPropagation();
                if (hasChildren) {
                  setExpanded((prev) => ({
                    ...prev,
                    [row.id]: !prev[row.id],
                  }));
                }
              }}
            >
              <Chevron open={isOpen} />
            </button>
            <span className="text-sm font-semibold capitalize text-[#111827]">
              {row.team_name || "—"}
            </span>
          </div>
        );
      },
    },
    {
      headerName: "Department",
      field: "departmentName",
      minWidth: 180,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-sm text-[#374151]">
          {params.row?.departmentName || "—"}
        </span>
      ),
    },
    {
      headerName: "Organization",
      field: "organizationIds",
      minWidth: 200,
      renderCell: (params: YhTableCellParams) => {
        const ids: string[] = params.row?.organizationIds || [];
        const labels = ids
          .map((id) => orgNameMap[id] || id.slice(-6))
          .filter(Boolean);
        return (
          <span className="text-sm text-[#374151]">
            {labels.length ? labels.join(", ") : "Yathaw (default)"}
          </span>
        );
      },
    },
    {
      headerName: "Actions",
      field: "actions",
      minWidth: 90,
      renderCell: (params: YhTableCellParams) => (
        <button
          type="button"
          data-action-button="true"
          className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F3F4F6]"
          onClick={(e) => {
            e.stopPropagation();
            const rect = (
              e.currentTarget as HTMLButtonElement
            ).getBoundingClientRect();
            setMenuPos({ top: rect.bottom + 4, left: rect.left - 160 });
            setSelectedRow(params.row);
            setMenuOpen(true);
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="5" r="1.5" />
            <circle cx="12" cy="12" r="1.5" />
            <circle cx="12" cy="19" r="1.5" />
          </svg>
        </button>
      ),
    },
  ];

  const isEdit = !!teamDrawerData._id;

  return (
    <div className="flex h-[calc(100vh-68px)] max-h-[calc(100vh-68px)] flex-col overflow-hidden bg-white p-4 sm:p-5 lg:p-6">
      <div className="mb-4 flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#1E1B4B] sm:text-3xl">
            Team List
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            Manage your team list here
          </p>
        </div>
        <YhButton variant="primary" size="sm" onClick={openCreate}>
          + Create Team
        </YhButton>
      </div>

      <div className="mb-3 shrink-0 sm:max-w-[420px]">
        <YhInput
          placeholder="Search teams…"
          value={search}
          type="search"
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden">
        <YhTable
          columns={columns}
          rows={visibleRows}
          getRowId={(row) => row.id}
          loading={loading}
          emptyMessage="No teams found"
          className="h-full"
          showPagination={false}
        />
      </div>

      {menuOpen && menuPos ? (
        <div
          ref={menuRef}
          className="fixed z-[1500] w-48 overflow-hidden rounded-xl border border-[#EEF0F5] bg-white py-1 shadow-[0_8px_24px_rgba(15,23,42,0.12)]"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          <p className="px-3 py-2 text-xs font-semibold uppercase text-[#9CA3AF]">
            Actions
          </p>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#111827] hover:bg-[#F8FAFC]"
            onClick={async () => {
              setMenuOpen(false);
              if (!selectedRow?.id) return;
              const response = await getTeamMetaByID(selectedRow.id);
              setTeamDrawerData({ ...response.data });
              setIsChecked(!!response.data?.parentTeamId);
              try {
                const orgRes = await listTeamOrganizations(selectedRow.id);
                setOrganizationIds(orgRes?.organizationIds || []);
              } catch (_) {
                setOrganizationIds(selectedRow.organizationIds || []);
              }
              setTeamMetaDrawer(true);
            }}
          >
            Edit
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[#DC2626] hover:bg-[#FEF2F2]"
            onClick={() => {
              setDialogOpen(true);
              setMenuOpen(false);
            }}
          >
            Delete
          </button>
        </div>
      ) : null}

      {teamMetaDrawer ? (
        <div
          className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 p-4"
          onClick={closeDrawer}
        >
          <div
            className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between border-b border-[#EEF0F5] px-5 py-4">
              <h2 className="text-lg font-bold text-[#111827]">
                {isEdit ? "Edit Team" : "Create Team"}
              </h2>
              <button
                type="button"
                onClick={closeDrawer}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F3F4F6]"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <YhInput
                    label="Team Name"
                    requiredMark
                    placeholder="Team Name"
                    value={teamDrawerData.team_name ?? ""}
                    error={errors.teamName ? "Team Name is required" : undefined}
                    onChange={(e) => {
                      setTeamDrawerData({
                        ...teamDrawerData,
                        team_name: e.target.value,
                      });
                      if (e.target.value) {
                        setErrors((prev) => ({ ...prev, teamName: false }));
                      }
                    }}
                  />
                  <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm text-[#374151]">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-[#D1D5DB]"
                      checked={isChecked}
                      onChange={(e) => {
                        setIsChecked(e.target.checked);
                        if (!e.target.checked) {
                          setTeamDrawerData({
                            ...teamDrawerData,
                            parentTeamId: null,
                            parentTeamName: "",
                          });
                        }
                      }}
                    />
                    Add as Sub Team
                  </label>
                </div>

                <div>
                  {isChecked ? (
                    <div>
                      <label className="yh-label">Parent Team Name</label>
                      <button
                        type="button"
                        className="yh-input w-full text-left"
                        onClick={() => setOpenTeamModal(true)}
                      >
                        {teamDrawerData?.parentTeamName || "Select"}
                      </button>
                    </div>
                  ) : null}
                </div>

                <div className="sm:col-span-2">
                  <OrganizationSelect
                    multiple
                    label="Organizations"
                    value={organizationIds}
                    onChange={setOrganizationIds}
                  />
                  <p className="mt-1 text-xs text-[#9CA3AF]">
                    Leave empty to default to Yathaw. Select multiple to share
                    this team across organizations.
                  </p>
                </div>

                <div className="sm:col-span-2">
                  <YhSelect
                    label="Department"
                    requiredMark
                    placeholder="Select Department"
                    value={teamDrawerData.departmentId || ""}
                    options={departmentList}
                    error={
                      errors.department ? "Department is required" : undefined
                    }
                    onChange={(e) => {
                      const selected = departmentList.find(
                        (d) => String(d.value) === e.target.value
                      );
                      setTeamDrawerData({
                        ...teamDrawerData,
                        departmentId: e.target.value,
                        departmentName: selected?.label || "",
                      });
                      if (e.target.value) {
                        setErrors((prev) => ({ ...prev, department: false }));
                      }
                    }}
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="yh-label">Roles</label>
                  <div className="max-h-48 overflow-y-auto rounded-xl border border-[#EEF0F5] p-3">
                    {teamRole.length === 0 ? (
                      <p className="text-sm text-[#9CA3AF]">No roles available</p>
                    ) : (
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {teamRole.map((option) => {
                          const checked = (teamDrawerData?.teamRole || []).some(
                            (m: any) => m.id === option.id
                          );
                          return (
                            <label
                              key={option.id}
                              className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-[#374151] hover:bg-[#F8FAFC]"
                            >
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-[#D1D5DB]"
                                checked={checked}
                                onChange={() => toggleRole(option)}
                              />
                              {option.label}
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex shrink-0 justify-end gap-3 border-t border-[#EEF0F5] bg-[#FAFBFC] px-5 py-4">
              <YhButton variant="secondary" size="sm" onClick={closeDrawer}>
                Cancel
              </YhButton>
              <YhButton
                variant="primary"
                size="sm"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? "Saving…" : "Submit"}
              </YhButton>
            </div>
          </div>
        </div>
      ) : null}

      {openTeamModal ? (
        <div
          className="fixed inset-0 z-[1500] flex items-center justify-center bg-black/40 p-4"
          onClick={() => setOpenTeamModal(false)}
        >
          <div
            className="flex max-h-[70vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-[#EEF0F5] px-5 py-4">
              <h3 className="text-base font-bold text-[#111827]">Select Team</h3>
              <button
                type="button"
                onClick={() => setOpenTeamModal(false)}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F3F4F6]"
              >
                ✕
              </button>
            </div>
            <div className="overflow-y-auto px-3 py-3">
              {teamData.map((team) => (
                <button
                  key={team.id}
                  type="button"
                  className={`mb-1 flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm hover:bg-[#F8FAFC] ${
                    selectedTeam === team.id ? "bg-[#F9F0F4]" : ""
                  }`}
                  style={{ paddingLeft: 12 + (team.path.length - 1) * 16 }}
                  onClick={() => handleParentTeam(team)}
                >
                  <span className="font-medium text-[#111827]">
                    {team.team_name}
                  </span>
                  <span
                    className={`h-4 w-4 rounded-full border ${
                      selectedTeam === team.id
                        ? "border-[#A02448] bg-[#A02448]"
                        : "border-[#D1D5DB]"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmationModel
        open={dialogOpen}
        title="Confirmation"
        description="Are you sure you want to delete?"
        onConfirm={handleDelete}
        onCancel={() => setDialogOpen(false)}
      />
    </div>
  );
};

export default TeamMetaList;
